// Used in timezone.helper.ts

import { Injectable } from "@angular/core";
import { CompositeTaskAssignment, PPAssignment as Assignment } from "cli-types";
import moment from "moment-timezone";
import momentTz from "moment-timezone";

@Injectable()
export class TimezoneHelper {
    private readonly dateFields = Object.freeze([
        // all date fields that should be converted
        "createdAt",
        "updatedAt",
        "deletedAt",
        "archivedAt",
        "queuedAt",
        "completedAt",
        "started",
        "completed",
        "gradeEdited",
        "completedAtRestored",
        "displayAt",
        "expireAt",
        "actionDate",
        "dueDate",
        "startDate",
        "teacherNotifiedAt",
        "notifiedAt",
        "evaluatedAt",
        "gradeEdited",
        "feedbackUpdatedAt",
        "readAt",
        "sentToBeiAt",
        "metricsUpdatedAt",
        "expireDate",
        "accessTokenExpireAt",
        "resetPasswordAt",
        "lastLoginAt",
        "currentLoginAt",
        "lastActiveAt",
        "activatedAt",
        "created_at",
        "updated_at",
        "startedAt",
    ]);

    constructor() {}

    /**
     * @method convertServerDateToUserTZ
     * @description returns an updated string of the current dateTime in the school TZ from the user TZ
     * @param rawDate
     * @param userTZ
     * @returns {string}
     */
    convertServerDateToUserTZ(
        rawDate: any,
        userTZ: string | undefined
    ): string {
        // TODO: Sort out exactly what rawDate might be other than a string. A moment?
        let date: any;
        if (rawDate && rawDate.date) {
            date = this.serverDateFormatToISOFormat(rawDate.date);
        } else if (rawDate instanceof Date) {
            const dateObjectToIso = rawDate.toISOString();
            date = this.serverDateFormatToISOFormat(dateObjectToIso);
        } else {
            date = this.serverDateFormatToISOFormat(rawDate);
        }
        date = momentTz.tz(date, "GMT"); // set date to server tz
        if (userTZ === undefined || userTZ === null) {
            // default tz if not set
            userTZ = "America/Chicago";
        }
        date = date.clone().tz(userTZ); // convert to school tz

        // remove the Z (tz offset) in the date
        // return the updated date time in the school's timezone
        return momentTz(date).format("YYYY-MM-DD HH:mm:ss");
    }

    /**
     * @method convertUserDateToServerTZ
     * @description returns an updated string of the current dateTime in the server TZ from the user TZ
     * @param rawDate
     * @param userTZ
     * @returns {string}
     */
    convertUserDateToServerTZ(
        rawDate: any,
        userTZ: string | undefined
    ): string {
        // TODO: Sort out exactly what rawDate might be other than a string. A moment?
        let date: any;
        if (rawDate && rawDate.date) {
            date = this.serverDateFormatToISOFormat(rawDate.date);
        } else {
            date = this.serverDateFormatToISOFormat(rawDate);
        }
        if (userTZ === undefined || userTZ === null) {
            // default tz if not set
            userTZ = "America/Chicago";
        }
        date = momentTz.tz(date, userTZ); // set date to school tz
        date = date.clone().tz("GMT"); // convert to server tz

        // remove the Z (tz offset) in the date
        // return the updated date time in the server's timezone
        return momentTz(date).format("YYYY-MM-DD HH:mm:ss");
    }

    /**
     * @method getNowDate
     * @description returns a moment object of the current dateTime in the userTZ
     * @param userTZ
     * @returns {any}
     */
    getNowDate(userTZ: string): moment.Moment {
        return momentTz().tz(userTZ); // convert to school tz from local client tz
    }

    /**
     * @method updateArrayObjServerTZToUserTZ
     * @description iterates through and array of objects and searches for date fields and converts from serverTZ to given userTZ,
     * only the top level is converted, nested levels will not be touched
     * @param rawArrayObj
     * @param userTZ
     * @returns {any}
     */
    updateArrayObjServerTZToUserTZ<
        T extends Assignment | CompositeTaskAssignment
    >(rawArrayObj: Array<T>, userTZ: string | undefined): Array<T> {
        return rawArrayObj.map((ass) =>
            this.updateObjServerTZToUserTZ(ass, userTZ)
        );
    }

    /**
     * @method updateArrayObjUserTZToServerTZ
     * @description iterates through and array of objects and searches for date fields and converts from userTZ to serverTZ,
     * only the top level is converted, nested levels will not be touched
     * @param rawArrayObj
     * @param userTZ
     * @returns {any}
     */
    updateArrayObjUserTZToServerTZ<
        T extends Assignment | CompositeTaskAssignment
    >(rawArrayObj: Array<T>, userTZ: string): Array<T> {
        return rawArrayObj.map((ass) =>
            this.updateObjServerTZToUserTZ(ass, userTZ, true)
        );
    }

    /**
     * @method updateObjServerTZToUserTZ
     * @description searches through given obj for date fields and converts from serverTZ to given userTZ
     * @param rawObj
     * @param userTZ
     * @param reverse {boolean} switches conversion to userTZ -> serverTZ instead of serverTZ -> userTZ
     * @returns {any}
     * ToDo: probably refactor this since it's very easy to overlook when adding new date fields.
     */
    updateObjServerTZToUserTZ(
        rawObj: any,
        userTZ: string | undefined,
        reverse = false
    ): any {
        //TODO: raw obj should be: Assignment | CompositeTaskAssignment
        for (const field of this.dateFields) {
            if (
                rawObj.hasOwnProperty(field) &&
                rawObj[field] !== null &&
                rawObj[field] !== "null" &&
                rawObj[field] !== "NULL"
            ) {
                if (reverse) {
                    rawObj = {
                        ...rawObj,
                        [field]: this.convertUserDateToServerTZ(
                            rawObj[field],
                            userTZ
                        ),
                    };
                } else {
                    rawObj = {
                        ...rawObj,
                        [field]: this.convertServerDateToUserTZ(
                            rawObj[field],
                            userTZ
                        ),
                    };
                }
            }
        }

        return rawObj;
    }

    /**
     * @method serverDateToISOFormat
     * @description converts the server date to client friendly ISO formatted date
     * @param {string} input
     * @returns {string}
     */
    private serverDateFormatToISOFormat(input: string) {
        // standard 24 hour datetimes with - and : (format: YYYY-MM-DD H:mm:ss [can also be HH instead of H, case covered below])
        let t = input.split(/[- :]/);
        if (t.length === 6) {
            for (let i = 1; i < t.length; i++) {
                // Loop through to add any missing leading zeros (skip index 0 which is year)
                if (t[i].length !== 2) {
                    t[i] = "0" + t[i]; // add missing leading zero
                }
            }

            // Date & Time needs formatting from DB format
            // returns ISO format YYYY-MM-DDTH:mm:ss
            input =
                t[0] +
                "-" +
                t[1] +
                "-" +
                t[2] +
                "T" +
                t[3] +
                ":" +
                t[4] +
                ":" +
                t[5];
        }
        // standard 12 hour datetimes with / and : (format: MM/DD/YYYY h:mm a [can also be hh instead of h, case covered below])
        t = input.split(/[\/ :]/);
        if (t.length === 6) {
            for (let i = 0; i < t.length; i++) {
                // Loop through to add any missing leading zeros (skip index 0 which is year)
                if (i === 2) {
                    continue;
                }

                if (t[i].length !== 2) {
                    t[i] = "0" + t[i]; // add missing leading zero
                }
            }

            // If PM add 12 hours and not noon
            if ((t[5] === "PM" || t[5] === "pm") && t[3] !== "12") {
                t[3] = (Number(t[3]) + 12).toString();
            } else if ((t[5] === "AM" || t[5] === "am") && t[3] === "12") {
                // If AM and midnight
                t[3] = "00";
            }

            // Date & Time needs formatting from DB format
            // returns ISO format YYYY-MM-DDTH:mm:ss
            input =
                t[2] +
                "-" +
                t[0] +
                "-" +
                t[1] +
                "T" +
                t[3] +
                ":" +
                t[4] +
                ":00";
        }
        return input;
    }

    getTimezones() {
        return [
            {
                name: "Atlantic Time",
                abbreviation: "America/Puerto_Rico",
                tz: "atlantic_time",
            },
            {
                name: "Eastern Time",
                abbreviation: "America/New_York",
                tz: "eastern_time",
            },
            {
                name: "Central Time",
                abbreviation: "America/Chicago",
                tz: "central_time",
            },
            {
                name: "Mountain Time",
                abbreviation: "America/Denver",
                tz: "mountain_time",
            },
            {
                name: "Mountain Time (no DST)",
                abbreviation: "America/Phoenix",
                tz: "mountain_time",
            },
            {
                name: "Pacific Time",
                abbreviation: "America/Los_Angeles",
                tz: "pacific_time",
            },
            {
                name: "Alaska Time",
                abbreviation: "America/Anchorage",
                tz: "alaska_time",
            },
            {
                name: "Hawaii-Aleutian",
                abbreviation: "America/Adak",
                tz: "hawaii_aleutian_time",
            },
            {
                name: "Hawaii-Aleutian Time (no DST)",
                abbreviation: "Pacific/Honolulu",
                tz: "hawaii_aleutian_time",
            },
            {
                name: "Greenwich Mean Time",
                abbreviation: "Etc/GMT+0",
                tz: "gmt",
            },
            {
                name: "Central European Time",
                abbreviation: "CET",
                tz: "cet",
            },
            {
                name: "South Korea Time",
                abbreviation: "Asia/Seoul",
                tz: "skt",
            },
            {
                name: "Japanese Standard Time",
                abbreviation: "Asia/Tokyo",
                tz: "jst",
            },
            {
                name: "Chamorro Time",
                abbreviation: "Pacific/Guam",
                tz: "chamorro_time",
            },
            {
                name: "China Standard Time",
                abbreviation: "Asia/Shanghai",
                tz: "china_time",
            },
            {
                name: "Indochina Time",
                abbreviation: "Asia/Bangkok",
                tz: "ict",
            },
        ];
    }
}
